import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, ShoppingBag, Heart, User, Menu, X, ChevronDown, LogOut, LayoutDashboard, Store, Shield, Package, MessageSquare } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useOrderAvailability } from '../../context/OrderAvailabilityContext';
import { Button } from '../ui/button';
import CurrencySelector from '../common/CurrencySelector';
import BiscuitBrandLogo from '../common/BiscuitBrandLogo';
import { assetUrl } from '../../lib/assetUrl';
import { toast } from 'sonner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { buildProductUrl } from '../../lib/productUrl';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mobile nav link with active state - accepts onNavigate callback to close drawer
const MobileNavLink = ({ to, children, className = '', exact = false, onNavigate }) => {
    const location = useLocation();
    const navigate = useNavigate();
    
    // For exact match (like /shop), only match exact path
    // For category links (like /shop/cookies), match the exact path
    const isActive = exact 
        ? location.pathname === to 
        : location.pathname === to || location.pathname.startsWith(to + '/');
    
    // Special case: /shop should only be active when exactly on /shop, not /shop/category
    const isShopRoot = to === '/shop';
    const actuallyActive = isShopRoot 
        ? location.pathname === '/shop' 
        : isActive;
    
    const handleClick = (e) => {
        e.preventDefault();
        // Close the drawer first
        if (onNavigate) {
            onNavigate();
        }
        // Then navigate
        navigate(to);
    };
    
    return (
        <a 
            href={to}
            onClick={handleClick}
            className={`${className} transition-all cursor-pointer ${
                actuallyActive 
                    ? 'text-primary font-semibold border-l-2 border-primary pl-3 -ml-3' 
                    : 'hover:text-primary'
            }`}
        >
            {children}
        </a>
    );
};

const Navbar = () => {
    const { user, isAuthenticated, logout, isSeller, isAdmin } = useAuth();
    const { itemCount } = useCart();
    const { ordersEnabled, orderMessage } = useOrderAvailability();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState({ products: [], categories: [], brands: [], countries: [], sellers: [] });
    const searchDebounceRef = useRef(null);
    const [showSearch, setShowSearch] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const searchRef = useRef(null);
    const mobileSearchRef = useRef(null);

    // Close all overlays on route change
    const location = useLocation();
    useEffect(() => {
        setMobileSearchOpen(false);
        setMobileMenuOpen(false);
        setSearchQuery('');
        setSearchResults({ products: [], categories: [], brands: [], countries: [], sellers: [] });
    }, [location.pathname]);

    // Close mobile menu on navigation
    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };
    
    // Close mobile search on navigation
    const closeMobileSearch = () => {
        setMobileSearchOpen(false);
        setSearchQuery('');
        setSearchResults({ products: [], categories: [], brands: [], countries: [] });
    };

    // Fetch unread message count
    useEffect(() => {
        if (!isAuthenticated) { setUnreadMessages(0); return; }
        const fetchUnread = async () => {
            try {
                const token = localStorage.getItem('auth_token');
                if (!token) return;
                const res = await fetch(`${API_URL}/messages/unread-count`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setUnreadMessages(data.unread || 0);
                }
            } catch (e) { /* silent */ }
        };
        fetchUnread();
        const interval = setInterval(fetchUnread, 30000);
        return () => clearInterval(interval);
    }, [isAuthenticated]);

    // Focus mobile search input reliably across all browsers
    // (autoFocus prop is ignored by Safari iOS, Samsung Internet, Firefox Android on conditional renders)
    useEffect(() => {
        if (mobileSearchOpen && mobileSearchRef.current) {
            // requestAnimationFrame ensures the DOM has painted the overlay before focusing
            requestAnimationFrame(() => {
                if (mobileSearchRef.current) {
                    mobileSearchRef.current.focus({ preventScroll: true });
                }
            });
        }
    }, [mobileSearchOpen]);
    useEffect(() => {
        if (mobileSearchOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = `-${window.scrollY}px`;
        } else {
            const scrollY = document.body.style.top;
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        }
        
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
        };
    }, [mobileSearchOpen]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                // Use the homepage-priority endpoint so the mobile
                // "Shop All" menu surfaces the commercially useful
                // categories first (Cookies, Chocolate Biscuits, ...)
                // then falls back to alphabetical for the rest. The
                // `?_=<ts>` cache-buster mirrors the homepage's fetch
                // so an admin re-prioritisation is visible immediately.
                // This is the ONLY user-facing surface (besides the
                // homepage grid) that uses the priority order — admin
                // lists, filters, product forms, and category directory
                // still call /api/categories (strict A→Z).
                const response = await fetch(`${API_URL}/categories/homepage?_=${Date.now()}`);
                if (response.ok) {
                    const data = await response.json();
                    setCategories(data.slice(0, 8));
                }
            } catch (error) {
                console.error('Failed to fetch categories:', error);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSearch(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    const handleSearch = (query) => {
        setSearchQuery(query);
        clearTimeout(searchDebounceRef.current);
        if (query.length < 2) {
            setSearchResults({ products: [], categories: [], brands: [], countries: [], sellers: [] });
            return;
        }
        searchDebounceRef.current = setTimeout(async () => {
            try {
                const response = await fetch(`${API_URL}/search/suggestions?q=${encodeURIComponent(query)}`);
                if (response.ok) {
                    const data = await response.json();
                    setSearchResults(data);
                }
            } catch (error) {
                console.error('Search failed:', error);
            }
        }, 300);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/shop?q=${encodeURIComponent(searchQuery)}`);
            setShowSearch(false);
            setSearchQuery('');
        }
    };

    const handleLogout = async () => {
        await logout();
        toast.success('Signed out successfully');
        navigate('/');
    };

    return (
        <header className={`sticky top-0 z-40 transition-all duration-300 w-full max-w-full ${isScrolled ? 'bg-background/95 backdrop-blur-md shadow-sm' : 'bg-background'}`}>
            {/* Order Disabled Banner — sticky top, non-dismissible */}
            {!ordersEnabled && (
                <div className="bg-amber-900 text-amber-50 py-3.5 px-4 text-center" data-testid="orders-disabled-banner">
                    <p className="text-sm font-semibold leading-relaxed tracking-wide max-w-3xl mx-auto">
                        {orderMessage || 'Ordering is temporarily paused. You can still browse products, register, and join as a seller.'}
                    </p>
                </div>
            )}
            {/* Top Bar */}
            <div className="bg-primary text-primary-foreground text-sm py-2 w-full">
                <div className="container-custom flex items-center justify-between">
                    <span>Worldwide shipping available</span>
                    <div className="hidden md:flex items-center gap-4">
                        <CurrencySelector variant="ghost" />
                        <Link to="/blog" className="hover:underline" data-testid="nav-blog-desktop">Blog</Link>
                        <Link to="/become-a-seller" className="hover:underline">Sell on Biscuit Brand</Link>
                        <Link to="/contact" className="hover:underline">Contact</Link>
                        <Link to="/faq" className="hover:underline">Help</Link>
                    </div>
                </div>
            </div>

            {/* Main Nav */}
            <nav className="container-custom py-4 w-full">
                <div className="flex items-center justify-between gap-2 md:gap-4 w-full">
                    {/* Logo */}
                    <div className="flex-shrink-0 min-w-0">
                        <BiscuitBrandLogo 
                          variant="compact" 
                          theme="dark" 
                          size="md" 
                          linkTo="/"
                          data-testid="logo-link"
                        />
                    </div>

                    {/* Search - Desktop */}
                    <div className="hidden md:flex flex-1 max-w-xl mx-8" ref={searchRef}>
                        <form onSubmit={handleSearchSubmit} className="relative w-full">
                            <input
                                type="search"
                                placeholder="Search for biscuits..."
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                                onFocus={() => setShowSearch(true)}
                                onClick={() => setShowSearch(true)}
                                className="w-full pl-10 pr-4 py-2.5 rounded-full border border-border bg-accent/50 focus:bg-card focus:border-primary transition-colors"
                                data-testid="search-input"
                            />
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            
                            {/* Search Dropdown */}
                            {showSearch && (searchResults.products.length > 0 || searchResults.categories.length > 0 || searchResults.brands?.length > 0 || searchResults.countries?.length > 0 || searchResults.sellers?.length > 0) && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-card/95 backdrop-blur-md rounded-lg shadow-lg border border-border overflow-hidden z-50 max-h-[400px] overflow-y-auto">
                                    {searchResults.categories.length > 0 && (
                                        <div className="p-3 border-b border-border">
                                            <p className="text-xs font-semibold text-foreground/70 uppercase tracking-wider mb-2">Categories</p>
                                            {searchResults.categories.map((cat) => (
                                                <Link
                                                    key={cat.slug}
                                                    to={`/shop/${cat.slug}`}
                                                    className="block py-1.5 px-2 -mx-2 rounded-md text-foreground hover:bg-accent/80 transition-colors"
                                                    onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                                                >
                                                    {cat.name}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                    {searchResults.brands?.length > 0 && (
                                        <div className="p-3 border-b border-border">
                                            <p className="text-xs font-semibold text-foreground/70 uppercase tracking-wider mb-2">Brands</p>
                                            {searchResults.brands.map((brand) => (
                                                <Link
                                                    key={brand}
                                                    to={`/shop?q=${encodeURIComponent(brand)}`}
                                                    className="block py-1.5 px-2 -mx-2 rounded-md text-foreground hover:bg-accent/80 transition-colors"
                                                    onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                                                >
                                                    {brand}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                    {searchResults.countries?.length > 0 && (
                                        <div className="p-3 border-b border-border">
                                            <p className="text-xs font-semibold text-foreground/70 uppercase tracking-wider mb-2">Countries</p>
                                            {searchResults.countries.map((country) => (
                                                <Link
                                                    key={country}
                                                    to={`/biscuits-by-country/${country.toLowerCase().replace(/\s+/g, '-')}`}
                                                    className="block py-1.5 px-2 -mx-2 rounded-md text-foreground hover:bg-accent/80 transition-colors"
                                                    onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                                                >
                                                    {country}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                    {searchResults.sellers?.length > 0 && (
                                        <div className="p-3 border-b border-border">
                                            <p className="text-xs font-semibold text-foreground/70 uppercase tracking-wider mb-2">Shops</p>
                                            {searchResults.sellers.map((seller) => (
                                                <Link
                                                    key={seller.seller_id}
                                                    to={`/store/${seller.slug || seller.seller_id}`}
                                                    className="flex items-center gap-3 py-1.5 px-2 -mx-2 rounded-md text-foreground hover:bg-accent/80 transition-colors"
                                                    onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                                                >
                                                    {seller.store_logo ? (
                                                        <img
                                                            src={assetUrl(seller.store_logo)}
                                                            alt=""
                                                            className="w-6 h-6 rounded-full object-contain bg-muted p-0.5 flex-shrink-0"
                                                            loading="lazy"
                                                            decoding="async"
                                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                        />
                                                    ) : (
                                                        <Store className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                                    )}
                                                    <span className="text-sm">{seller.store_name}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                    {searchResults.products.length > 0 && (
                                        <div className="p-3">
                                            <p className="text-xs font-semibold text-foreground/70 uppercase tracking-wider mb-2">Products</p>
                                            {searchResults.products.map((product) => (
                                                <Link
                                                    key={product.product_id}
                                                    to={buildProductUrl(product)}
                                                    className="flex items-center gap-3 py-2 hover:bg-accent/80 rounded-md px-2 -mx-2 transition-colors"
                                                    onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                                                >
                                                    {/* Thumbnail — mirrors ProductCard exactly: same
                                                        `assetUrl()` resolution so /uploads/ paths hit
                                                        the API host instead of the SPA (which would
                                                        return index.html and render as a black box).
                                                        Always rendered so the row layout stays
                                                        consistent; an onError fallback handles broken
                                                        URLs without a black empty box. */}
                                                    <div className="w-10 h-10 rounded bg-accent overflow-hidden flex-shrink-0">
                                                        <img
                                                            src={assetUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=120'}
                                                            alt={product.name}
                                                            className="w-full h-full object-contain"
                                                            loading="lazy"
                                                            decoding="async"
                                                            onError={(e) => {
                                                                if (e.currentTarget.dataset.fellback) return;
                                                                e.currentTarget.dataset.fellback = '1';
                                                                e.currentTarget.src = 'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=120';
                                                            }}
                                                        />
                                                    </div>
                                                    <span className="text-sm text-foreground font-medium">{product.name}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                    {searchQuery && (
                                        <div className="p-3 border-t border-border">
                                            <button
                                                type="submit"
                                                className="text-sm text-primary hover:underline w-full text-left"
                                            >
                                                Search for "{searchQuery}" →
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Desktop No Results */}
                            {showSearch && searchQuery.length >= 2 && searchResults.products.length === 0 && searchResults.categories.length === 0 && !searchResults.brands?.length && !searchResults.countries?.length && !searchResults.sellers?.length && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-card/95 backdrop-blur-md rounded-lg shadow-lg border border-border overflow-hidden z-50 p-4 text-center">
                                    <p className="text-sm text-muted-foreground">No results found for "{searchQuery}"</p>
                                    <p className="text-xs text-muted-foreground mt-1">Try a different search or <button type="button" className="text-primary hover:underline" onClick={() => { setShowSearch(false); setSearchQuery(''); navigate(`/shop?q=${encodeURIComponent(searchQuery)}`); }}>view all results</button></p>
                                </div>
                            )}
                            {/* Desktop Initial State - shown when focused with no results */}
                            {showSearch && searchQuery.length < 2 && searchResults.products.length === 0 && searchResults.categories.length === 0 && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-card/95 backdrop-blur-md rounded-lg shadow-lg border border-border overflow-hidden z-50 p-6 text-center">
                                    <Search className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                                    <p className="text-sm font-medium text-foreground/80">Start typing to search</p>
                                    <p className="text-xs mt-1 text-muted-foreground">Search for products, categories, brands, countries, or stores</p>
                                </div>
                            )}
                        </form>
                    </div>

                    {/* Actions - shrink-0 ensures icons don't get pushed off screen */}
                    <div className="flex items-center gap-0.5 sm:gap-1 md:gap-4 flex-shrink-0">
                        {/* Mobile Search */}
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="md:hidden h-8 w-8 sm:h-9 sm:w-9" 
                            onClick={() => setMobileSearchOpen(true)}
                            data-testid="mobile-search-btn"
                        >
                            <Search className="w-4 h-4 sm:w-5 sm:h-5" />
                        </Button>

                        {/* Wishlist - hide on smallest mobile */}
                        {isAuthenticated && (
                            <Link to="/wishlist" data-testid="wishlist-link" className="hidden sm:block">
                                <Button variant="ghost" size="icon" className="h-9 w-9">
                                    <Heart className="w-5 h-5" />
                                </Button>
                            </Link>
                        )}

                        {/* Messages */}
                        {isAuthenticated && (
                            <Link to="/messages" className="relative" data-testid="messages-link">
                                <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9">
                                    <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                                    {unreadMessages > 0 && (
                                        <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-4 h-4 sm:w-5 sm:h-5 bg-primary text-primary-foreground text-[10px] sm:text-xs rounded-full flex items-center justify-center">
                                            {unreadMessages > 9 ? '9+' : unreadMessages}
                                        </span>
                                    )}
                                </Button>
                            </Link>
                        )}

                        {/* Cart */}
                        <Link to="/cart" className="relative" data-testid="cart-link">
                            <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9">
                                <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5" />
                                {itemCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-4 h-4 sm:w-5 sm:h-5 bg-primary text-primary-foreground text-[10px] sm:text-xs rounded-full flex items-center justify-center">
                                        {itemCount}
                                    </span>
                                )}
                            </Button>
                        </Link>

                        {/* User Menu */}
                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" data-testid="user-menu-btn">
                                        <User className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <div className="px-3 py-2">
                                        <p className="font-medium">{user?.name}</p>
                                        <p className="text-sm text-muted-foreground">{user?.email}</p>
                                        <p className="text-xs text-muted-foreground/70 capitalize mt-0.5" data-testid="user-role-badge">
                                            {user?.role === 'admin' ? '👑 Admin' : user?.role === 'seller' ? '🏪 Seller' : '👤 Buyer'}
                                        </p>
                                    </div>
                                    <DropdownMenuSeparator />
                                    
                                    {/* Primary Dashboard Link - Role Based */}
                                    {isAdmin ? (
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin" className="cursor-pointer">
                                                <Shield className="w-4 h-4 mr-2" />
                                                Admin Dashboard
                                            </Link>
                                        </DropdownMenuItem>
                                    ) : isSeller ? (
                                        <DropdownMenuItem asChild>
                                            <Link to="/seller" className="cursor-pointer">
                                                <Store className="w-4 h-4 mr-2" />
                                                Seller Dashboard
                                            </Link>
                                        </DropdownMenuItem>
                                    ) : (
                                        <DropdownMenuItem asChild>
                                            <Link to="/account" className="cursor-pointer">
                                                <LayoutDashboard className="w-4 h-4 mr-2" />
                                                My Account
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    
                                    {/* Secondary Links */}
                                    {(isAdmin || isSeller) && (
                                        <DropdownMenuItem asChild>
                                            <Link to="/account" className="cursor-pointer">
                                                <LayoutDashboard className="w-4 h-4 mr-2" />
                                                My Account
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    
                                    <DropdownMenuItem asChild>
                                        <Link to="/account/orders" className="cursor-pointer">
                                            <Package className="w-4 h-4 mr-2" />
                                            My Orders
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/messages" className="cursor-pointer">
                                            <MessageSquare className="w-4 h-4 mr-2" />
                                            Messages
                                            {unreadMessages > 0 && (
                                                <span className="ml-auto bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                                                    {unreadMessages}
                                                </span>
                                            )}
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/account/wishlist" className="cursor-pointer">
                                            <Heart className="w-4 h-4 mr-2" />
                                            Wishlist
                                        </Link>
                                    </DropdownMenuItem>
                                    
                                    {/* Additional Role Links */}
                                    {isAdmin && isSeller && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem asChild>
                                                <Link to="/seller" className="cursor-pointer">
                                                    <Store className="w-4 h-4 mr-2" />
                                                    Seller Dashboard
                                                </Link>
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                    
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Link to="/login" data-testid="login-link">
                                <Button variant="default" size="sm" className="hidden md:inline-flex">
                                    Sign In
                                </Button>
                                <Button variant="ghost" size="icon" className="md:hidden h-9 w-9">
                                    <User className="w-5 h-5" />
                                </Button>
                            </Link>
                        )}

                        {/* Mobile Menu - Always visible on mobile, rightmost position */}
                        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" className="md:hidden h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0" data-testid="mobile-menu-btn">
                                    <Menu className="w-4 h-4 sm:w-5 sm:h-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                                <nav className="flex flex-col gap-4 mt-8">
                                    <MobileNavLink to="/shop" className="text-lg font-medium" onNavigate={closeMobileMenu}>
                                        Shop All
                                    </MobileNavLink>
                                    {categories.filter(c => c.slug !== 'gift-boxes').map((cat) => (
                                        <MobileNavLink
                                            key={cat.slug}
                                            to={`/shop/${cat.slug}`}
                                            className="text-lg"
                                            onNavigate={closeMobileMenu}
                                        >
                                            {cat.name}
                                        </MobileNavLink>
                                    ))}
                                    <hr className="my-2" />
                                    <MobileNavLink to="/about" onNavigate={closeMobileMenu}>About Us</MobileNavLink>
                                    <MobileNavLink to="/blog" onNavigate={closeMobileMenu}>Blog</MobileNavLink>
                                    <MobileNavLink to="/contact" onNavigate={closeMobileMenu}>Contact</MobileNavLink>
                                    <MobileNavLink to="/faq" onNavigate={closeMobileMenu}>FAQ</MobileNavLink>
                                    <MobileNavLink to="/seller-policy" onNavigate={closeMobileMenu}>Seller Policy</MobileNavLink>
                                    <MobileNavLink to="/become-a-seller" onNavigate={closeMobileMenu}>Sell on Biscuit Brand</MobileNavLink>
                                    {!isSeller && isAuthenticated && (
                                        <MobileNavLink to="/become-seller" onNavigate={closeMobileMenu}>Become a Seller</MobileNavLink>
                                    )}
                                </nav>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>

                {/* Category Nav - Desktop - Removed per user request */}
            </nav>
            
            {/* Mobile Search Overlay */}
            {mobileSearchOpen && (
                <div 
                    className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-xl md:hidden flex flex-col"
                    onTouchMove={(e) => {
                        // Prevent background scroll but allow scroll inside results panel
                        if (!e.target.closest('[data-scroll-region]')) {
                            e.preventDefault();
                        }
                    }}
                >
                    {/* Search Header - Glass */}
                    <div className="flex-shrink-0 p-4 border-b border-white/10 bg-white/80 backdrop-blur-2xl shadow-sm">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={closeMobileSearch}
                                className="p-2 hover:bg-black/5 rounded-lg flex-shrink-0"
                                data-testid="close-mobile-search"
                            >
                                <X className="w-5 h-5 text-neutral-800" />
                            </button>
                            <form 
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    if (searchQuery.trim()) {
                                        navigate(`/shop?q=${encodeURIComponent(searchQuery)}`);
                                        closeMobileSearch();
                                    }
                                }} 
                                className="flex-1"
                            >
                                <div className="relative">
                                    <input
                                        ref={mobileSearchRef}
                                        type="search"
                                        placeholder="Search for biscuits..."
                                        value={searchQuery}
                                        onChange={(e) => handleSearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-neutral-200 bg-white/90 text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:border-primary focus:ring-1 focus:ring-primary/20"
                                        data-testid="mobile-search-input"
                                    />
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                                </div>
                            </form>
                        </div>
                    </div>
                    
                    {/* Search Results - Scrollable glass panel */}
                    <div 
                        className="flex-1 overflow-y-auto overscroll-contain"
                        data-scroll-region
                        style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      <div className="m-3 mt-2 rounded-lg bg-amber-50/80 backdrop-blur-2xl shadow-lg border border-amber-100/30 p-4">
                        {/* Results content */}
                        {(searchResults.products.length > 0 || searchResults.categories.length > 0 || searchResults.brands?.length > 0 || searchResults.countries?.length > 0 || searchResults.sellers?.length > 0) && (
                            <>
                                {searchResults.categories.length > 0 && (
                                    <div className="mb-5">
                                        <p className="text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Categories</p>
                                        <div className="space-y-0.5">
                                            {searchResults.categories.map((cat) => (
                                                <Link
                                                    key={cat.slug}
                                                    to={`/shop/${cat.slug}`}
                                                    className="block py-2.5 px-3 rounded-lg text-neutral-800 font-medium hover:bg-amber-100/60 active:bg-amber-100/90 transition-colors"
                                                    onClick={closeMobileSearch}
                                                >
                                                    {cat.name}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {searchResults.brands?.length > 0 && (
                                    <div className="mb-5">
                                        <p className="text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Brands</p>
                                        <div className="space-y-0.5">
                                            {searchResults.brands.map((brand) => (
                                                <Link
                                                    key={brand}
                                                    to={`/shop?q=${encodeURIComponent(brand)}`}
                                                    className="block py-2.5 px-3 rounded-lg text-neutral-800 font-medium hover:bg-amber-100/60 active:bg-amber-100/90 transition-colors"
                                                    onClick={closeMobileSearch}
                                                >
                                                    {brand}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {searchResults.countries?.length > 0 && (
                                    <div className="mb-5">
                                        <p className="text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Countries</p>
                                        <div className="space-y-0.5">
                                            {searchResults.countries.map((country) => (
                                                <Link
                                                    key={country}
                                                    to={`/biscuits-by-country/${country.toLowerCase().replace(/\s+/g, '-')}`}
                                                    className="block py-2.5 px-3 rounded-lg text-neutral-800 font-medium hover:bg-amber-100/60 active:bg-amber-100/90 transition-colors"
                                                    onClick={closeMobileSearch}
                                                >
                                                    {country}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {searchResults.sellers?.length > 0 && (
                                    <div className="mb-5">
                                        <p className="text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Stores</p>
                                        <div className="space-y-0.5">
                                            {searchResults.sellers.map((seller) => (
                                                <Link
                                                    key={seller.seller_id}
                                                    to={`/store/${seller.slug || seller.seller_id}`}
                                                    className="flex items-center gap-3 py-2.5 px-3 rounded-lg text-neutral-800 font-medium hover:bg-amber-100/60 active:bg-amber-100/90 transition-colors"
                                                    onClick={closeMobileSearch}
                                                >
                                                    {seller.store_logo ? (
                                                        <img
                                                            src={assetUrl(seller.store_logo)}
                                                            alt=""
                                                            className="w-6 h-6 rounded-full object-contain bg-muted p-0.5 flex-shrink-0"
                                                            loading="lazy"
                                                            decoding="async"
                                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                        />
                                                    ) : (
                                                        <Store className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                                                    )}
                                                    <span>{seller.store_name}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {searchResults.products.length > 0 && (
                                    <div>
                                        <p className="text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Products</p>
                                        <div className="space-y-0.5">
                                            {searchResults.products.map((product) => (
                                                <Link
                                                    key={product.product_id}
                                                    to={buildProductUrl(product)}
                                                    className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-amber-100/60 active:bg-amber-100/90 transition-colors"
                                                    onClick={closeMobileSearch}
                                                >
                                                    {/* Same fix as desktop above — see comment there. */}
                                                    <div className="w-12 h-12 rounded-lg bg-accent overflow-hidden flex-shrink-0 shadow-sm">
                                                        <img
                                                            src={assetUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=160'}
                                                            alt={product.name}
                                                            className="w-full h-full object-contain"
                                                            loading="lazy"
                                                            decoding="async"
                                                            onError={(e) => {
                                                                if (e.currentTarget.dataset.fellback) return;
                                                                e.currentTarget.dataset.fellback = '1';
                                                                e.currentTarget.src = 'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=160';
                                                            }}
                                                        />
                                                    </div>
                                                    <span className="text-neutral-800 font-medium">{product.name}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        
                        {/* Empty state */}
                        {searchQuery.length >= 2 && searchResults.products.length === 0 && searchResults.categories.length === 0 && !searchResults.sellers?.length && !searchResults.brands?.length && !searchResults.countries?.length && (
                            <div className="text-center py-8">
                                <Search className="w-10 h-10 mx-auto mb-3 text-neutral-400" />
                                <p className="text-neutral-700 font-medium">No results found for "{searchQuery}"</p>
                                <p className="text-sm mt-1.5 text-neutral-500">Try a different search term</p>
                            </div>
                        )}
                        
                        {/* Initial state */}
                        {searchQuery.length < 2 && (
                            <div className="text-center py-8">
                                <Search className="w-10 h-10 mx-auto mb-3 text-neutral-400" />
                                <p className="text-neutral-700 font-medium">Start typing to search</p>
                                <p className="text-sm mt-1.5 text-neutral-500">Search for products, categories, brands, countries, or stores</p>
                            </div>
                        )}
                        {/* No results */}
                        {searchQuery.length >= 2 && searchResults.products.length === 0 && searchResults.categories.length === 0 && !searchResults.brands?.length && !searchResults.countries?.length && !searchResults.sellers?.length && (
                            <div className="text-center py-8">
                                <p className="text-neutral-700 font-medium">No results for "{searchQuery}"</p>
                                <p className="text-sm mt-1.5 text-neutral-500">Try a different search or browse all biscuits</p>
                            </div>
                        )}
                      </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
